import download from "downloadjs";
import React, { useCallback, useEffect, useState } from "react";
import { NotificationManager } from "react-notifications";
import Button from "../../components/Button/Button";
import CadastroContainer from "../../components/CadastroContainer/CadastroContainer";
import Card from "../../components/Card/Card";
import Input from "../../components/Input/Input";
import List from "../../components/List/List";
import PageTitle from "../../components/PageTitle/PageTitle";
import Pagination from "../../components/Pagination/Pagination";
import { useListManager } from "../../contexts/list.manager";
import LogsService from "../../services/LogsService";
import EventDetails from "../Events/EventDetails/EventDetails";
import "./Logs.css";
import { TimeAgo } from "../../components/TimeAgo/TimeAgo";
import { formatDate } from "../../utils/date";
import { IoDocumentTextOutline } from "react-icons/io5";
import Container from "../../components/Container/Container";
import SearchContainer from "../../components/SearchContainer/SearchContainer";
import SearchInput from "../../components/SearchInput/SearchInput";
import { useTranslation } from "react-i18next";
import { debounce } from "../../utils/timer";
import FilterLogs from "./FilterLogs";
import { LogOperationColor, LogOperationIcon, LogOperationKey, LogOperationText } from "../../constants/logs";
import { CardStat } from "../../components/CardStat/CardStat";
import { LuActivity } from "react-icons/lu";
import { formatNumber } from "../../utils/number";

const Logs = () => {
	const [logs, setLogs] = useState([]);
	const [collumnClicked, setCollumnClicked] = useState({
		collumnTitle: "id",
		orderDirection: "DESC",
	});
	const [maxPage, setMaxPage] = useState(1);
	const [rowsPerPage, setRowsPerPage] = useState(12);
	const [total, setTotal] = useState(0);
	const [totalPosts, setTotalPosts] = useState(0);
	const [totalPuts, setTotalPuts] = useState(0);
	const [totalDeletes, setTotalDeletes] = useState(0);
	const [currentPage, setCurrentPage] = useState(1);
	const { lineSelected, isCard } = useListManager();
	const [isLoading, setIsLoading] = useState(true);
	const { t } = useTranslation();
	const [search, setSearch] = useState("");
	const [isExporting, setIsExporting] = useState(false);
	const [exportedBytes, setExportedBytes] = useState(0);
	const [filters, setFilters] = useState({
		search: "",
		id: "",
		user_name: "",
		user_email: "",
		user_type: "",
		startDate: "",
		endDate: "",
		startTime: "",
		endTime: "",
		url_requested: "",
		operation: "",
		data: "",
		entityType: "",
	});

	const fetchLogs = async (page, filters, collumnClicked, rowsPerPage) => {
		setIsLoading(true);
		const response = await LogsService.listLogsPaginated(
			page,
			filters.search,
			collumnClicked,
			rowsPerPage,
			filters,
		);
		setLogs(response.data.results);
		setIsLoading(false);
		setTotal(response.data.total);
		setTotalPosts(Number(response.data.operationsCount.find(op => op.operation === LogOperationKey.CREATE)?.quantity ?? 0));
		setTotalPuts(Number(response.data.operationsCount.find(op => op.operation === LogOperationKey.EDIT)?.quantity ?? 0));
		setTotalDeletes(Number(response.data.operationsCount.find(op => op.operation === LogOperationKey.DELETE)?.quantity ?? 0));
		setMaxPage(Math.ceil(response.data.total / rowsPerPage));
	};

	const downloadCsv = async () => {
		const filename = `${new Date().toISOString().slice(0, 10)}-data.csv`;
		let fileHandle;

		if (window.showSaveFilePicker) {
			try {
				fileHandle = await window.showSaveFilePicker({
					suggestedName: filename,
					types: [{
						description: "CSV",
						accept: { "text/csv": [".csv"] },
					}],
				});
			} catch (error) {
				if (error.name === "AbortError") {
					return;
				}
				NotificationManager.error("", "Não foi possível iniciar o download do CSV", 5000);
				return;
			}
		}

		setIsExporting(true);
		setExportedBytes(0);
		let fileWriter;
		const chunks = [];

		try {
			const response = await LogsService.downloadCsv(filters, collumnClicked);
			if (!response?.body) {
				return;
			}

			fileWriter = fileHandle ? await fileHandle.createWritable() : null;
			const bom = new TextEncoder().encode("\uFEFF");
			if (fileWriter) {
				await fileWriter.write(bom);
			} else {
				chunks.push(bom);
			}

			const reader = response.body.getReader();
			while (true) {
				const { done, value } = await reader.read();
				if (done) {
					break;
				}

				setExportedBytes((bytes) => bytes + value.byteLength);
				if (fileWriter) {
					await fileWriter.write(value);
				} else {
					chunks.push(value);
				}
			}

			if (fileWriter) {
				await fileWriter.close();
			} else {
				download(
					new Blob(chunks, { type: "text/csv;charset=utf-8;" }),
					filename,
					"text/csv;charset=utf-8;",
				);
			}
		} catch (error) {
			if (fileWriter) {
				try {
					await fileWriter.abort();
				} catch (abortError) {
					console.error("Erro ao cancelar a gravação do CSV", abortError);
				}
			}
			console.error("Erro ao exportar logs para CSV", error);
			NotificationManager.error("", "Erro ao exportar logs para CSV", 5000);
		} finally {
			setIsExporting(false);
		}
	};

	useEffect(() => {
		fetchLogs(currentPage, filters, collumnClicked, rowsPerPage);
	}, [
		filters,
		collumnClicked,
		currentPage,
		rowsPerPage,
	]);

	const searchLogsHandler = () => {
		setCurrentPage(1);
		setFilters(prev => ({ ...prev, search }));
	};

	return (
		<div className="consulta-container">
			<div className="search-list-wrapper">
				<SearchContainer
					icon={<IoDocumentTextOutline size={20} />}
					title={`Registro de acessos da Cloud`}
					description={t("equipment.searchByName")}
					className="mb-0!"
					containerClassName="pb-0!"
					hideAddButton={true} />
				<div className="w-full">
					<FilterLogs
						filtersApplied={(filters) => {
							setFilters(filters);
							setCurrentPage(1);
						}}
						onDownloadCsv={downloadCsv}
						isExporting={isExporting}
						exportedBytes={exportedBytes}
					/>
				</div>
				<Container className="pb-0!">
					<div className="w-full grid grid-cols-4 gap-3 -mt-2! mb-0!">
						<CardStat
							title={LogOperationText[LogOperationKey.CREATE]}
							icon={LogOperationIcon[LogOperationKey.CREATE]}
							value={`${formatNumber(totalPosts)}`}
							color={`${LogOperationColor[LogOperationKey.CREATE]}`}
							onClick={(e) => setFilters(prev => ({ ...prev, operation: LogOperationKey.CREATE }))}
						/>
						<CardStat
							title={LogOperationText[LogOperationKey.EDIT]}
							icon={LogOperationIcon[LogOperationKey.EDIT]}
							value={`${formatNumber(totalPuts)}`}
							color={`${LogOperationColor[LogOperationKey.EDIT]}`}
							onClick={(e) => setFilters(prev => ({ ...prev, operation: LogOperationKey.EDIT }))}
						/>
						<CardStat
							title={LogOperationText[LogOperationKey.DELETE]}
							icon={LogOperationIcon[LogOperationKey.DELETE]}
							value={`${formatNumber(totalDeletes)}`}
							color={`${LogOperationColor[LogOperationKey.DELETE]}`}
							onClick={(e) => setFilters(prev => ({ ...prev, operation: LogOperationKey.DELETE }))}
						/>
						<CardStat
							title='Todos'
							icon={LuActivity}
							value={`${formatNumber(totalPosts + totalPuts + totalDeletes)}`}
							color={`var(--noDeviceLicensesColor)`}
							onClick={(e) => setFilters(prev => ({ ...prev, operation: null }))}
						/>
					</div>
				</Container>
				<Container className="mt-1!">
					<div className="list-container list-visibility">
						<List
							isLoading={isLoading}
							selectable={false}
							handleCollumnTitleClick={(collumnIndex) =>
								setCollumnClicked(collumnIndex)
							}
							collumsObjIndex={[
								{ collumnLabel: "Usuário", index: "user_name", type: "text" },
								{ collumnLabel: "Email", index: "user_email", type: "text" },
								{ collumnLabel: "Perfil", index: "user_type", type: "text" },
								{
									collumnLabel: "Data",
									index: "date",
									type: "custom",
									custom: ({ row }) => {
										return (
											<div className='flex flex-row items-center gap-1!'>
												<span>{formatDate(new Date(row.date), true)}</span>
												<span className='text-xs! opacity-60!'>
													(<TimeAgo date={row.date} simple={true} showText={true} />)
												</span>
											</div>
										);
									}
								},
								{
									collumnLabel: "Operação",
									index: "operation",
									type: "custom",
									custom: ({ row }) => (
										<span style={{ '--operationColor': LogOperationColor[row?.operation], '--operationBgColor': 'color-mix(in srgb, var(--operationColor), transparent 95%)' }}>
											{row?.operation ? (
												<span className="inline-flex flex-row gap-2 items-center px-3! py-2! border! rounded-full border-(--operationColor)! bg-(--operationBgColor)! text-(--operationColor)!">
													{React.createElement(LogOperationIcon[row.operation])}
													{row.operation}
												</span>
											) : (
												<span className="opacity-40">Não consta</span>
											)}
										</span>
									)
								},
							]}
							expandableContent={({ row, toggleExpanded }) => {
								return (
									<div className="events-details-spacing">
										<EventDetails
											title={"Detalhes do Registro"}
											event={row}
											isLog={true}
											close={() => toggleExpanded(row.id)}
										/>
									</div>
								);
							}}
							data={logs?.map(log => {
								return {
									...log,
									_expandable_: true,
									_expandableId_: log.id,
									_expandableLabel_: "Detalhes"
								};
							})}
							listEntityName={"logs"}
						/>
					</div>
					<div className="card-visibility">
						{logs &&
							logs.map((log, index) => (
								<Card key={index} data={logs} hideCardEditDeleteButtons={true}>
									<p className="card-content-title">{log.user_name}</p>
									<p className="card-content">Nome: {log.user_name}</p>
									<p className="card-content">Email: {log.user_email}</p>
									<p className="card-content">
										Tipo de Usuário: {log.user_type}
									</p>
									<p className="card-content">Operação: {log.operation}</p>
								</Card>
							))}
					</div>
					<Pagination
						currentPage={currentPage}
						rowsPerPage={rowsPerPage}
						maxPage={maxPage}
						total={total}
						changePage={(page) =>
							setCurrentPage(page)
						}
						changeRowsPerPage={(rowsPerPage) =>
							setRowsPerPage(rowsPerPage)
						}
					/>
				</Container>
			</div>
		</div>
	);
};

export default Logs;
